// frontend/src/components/PropostaDocumentoSheet.js
// Painel lateral com o documento da proposta: o HTML que sai no PDF, num
// iframe (srcdoc), com Baixar PDF / Imprimir / Converter. Na proposta de
// financiamento, a etapa e, para o time de Financiamentos, as ações dela.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Download, Printer, ArrowRightCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/contexts/AuthContext';
import { ACAO_FINANCIAMENTO } from '@/components/FinanciamentoDialog';
import { EtapaFinanciamentoChip, etapaVisivel } from '@/components/PropostaStatusChips';
import { formatDataHora } from '@/lib/format';
import { condicaoFinanciamento } from '@/lib/pagamento';

export default function PropostaDocumentoSheet({
  proposta, open, onOpenChange, onBaixarPdf, baixando = false, onConverter, podeConverter = false,
  acoesFinanciamento = [], onFinanciamento,
}) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const iframeRef = useRef(null);
  const etapa = etapaVisivel(proposta);
  const fin = proposta?.financiamento || {};
  // Quem mexeu por último; no aprovado, a condição que a financeira aprovou.
  const detalheEtapa = [
    etapa === 'aprovado' && condicaoFinanciamento(fin),
    fin.atualizado_por_nome && `por ${fin.atualizado_por_nome}`,
    fin.atualizado_em && formatDataHora(fin.atualizado_em),
  ].filter(Boolean).join(' · ');

  useEffect(() => {
    if (!open || !proposta?.id) return undefined;
    let cancelado = false;
    // Zera o documento anterior: o painel fica montado entre aberturas — sem
    // isto a proposta A continuaria visível (e imprimível) enquanto B carrega.
    setHtml('');
    setLoading(true);
    axios.get(`${API_URL}/propostas/${proposta.id}/documento`, { responseType: 'text' })
      .then((r) => { if (!cancelado) setHtml(r.data); })
      .catch(() => { if (!cancelado) toast.error('Não foi possível carregar o documento'); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [open, proposta?.id]);

  // Rede de segurança do PDF: a própria template tem @page A4 — o navegador
  // imprime/salva como PDF a partir do iframe.
  const imprimir = () => iframeRef.current?.contentWindow?.print();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl dark:bg-slate-950">
        <SheetHeader className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3 pr-6">
            <div className="min-w-0">
              <SheetTitle className="text-base">{proposta?.numero_proposta}</SheetTitle>
              <SheetDescription>{proposta?.dados?.cliente_nome}</SheetDescription>
              {etapa && (
                <div className="mt-2 space-y-0.5">
                  <EtapaFinanciamentoChip proposta={proposta} comMotivo={false} />
                  {etapa === 'negado' && fin.motivo && (
                    <p className="text-[13px] text-gray-700 dark:text-gray-300">Motivo: {fin.motivo}</p>
                  )}
                  {detalheEtapa && <p className="text-[12.5px] text-gray-500">{detalheEtapa}</p>}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {acoesFinanciamento.map((acao) => {
                const { label, icon: Icone } = ACAO_FINANCIAMENTO[acao];
                return (
                  <Button
                    key={acao} size="sm" variant={acao === 'aprovado' ? 'default' : 'outline'} onClick={() => onFinanciamento?.(acao)}
                    className={acao === 'negado' ? 'text-red-700 hover:text-red-800 dark:text-red-300' : ''}
                    data-testid={`documento-financiamento-${acao}`}
                  >
                    <Icone className="mr-1.5 h-4 w-4" /> {label}
                  </Button>
                );
              })}
              <Button size="sm" variant="outline" onClick={imprimir} disabled={!html} title="Imprimir / Salvar como PDF pelo navegador">
                <Printer className="mr-1.5 h-4 w-4" /> Imprimir
              </Button>
              <Button size="sm" variant="outline" onClick={() => onBaixarPdf?.(proposta)} disabled={baixando}>
                {baixando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                Baixar PDF
              </Button>
              {podeConverter && (
                <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-500" onClick={() => onConverter?.(proposta)}>
                  <ArrowRightCircle className="mr-1.5 h-4 w-4" /> Converter em pedido
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>
        <div className="relative flex-1 bg-slate-100 dark:bg-slate-900">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          <iframe ref={iframeRef} title="Documento da proposta" srcDoc={html} className="h-full w-full border-0 bg-white" />
        </div>
      </SheetContent>
    </Sheet>
  );
}
